import { useEffect, useMemo, useState } from 'react';
import moment from 'moment-timezone';
import { useQueryClient } from '@tanstack/react-query';
import { useProject } from '../../hooks/useProject';
import { useReviewStatistics, useSetReviewDateRange } from '../../hooks/useReview';
import { useProjectBatches, type Batch } from '../../hooks/useProjectBatches';
import { IioDataGrid } from '../../components/IioDataGrid';
import { hasPermission } from '../../types/project';
import { api } from '../../services/api';
import { showApiErrorToast } from '../../services/errorToast';
import { AssignBatchModal } from './AssignBatchModal';
import { EditBatchModal } from './EditBatchModal';
import { PaginationControls } from '../../components/PaginationControls';
import { LoadingPlaceholder } from '../../components/LoadingPlaceholder';
import { fetchingStyle } from '../../utilities/fetchingStyle';
import { BurnDownChart } from '../../components/charts/BurnDownChart';
import { buildBatchColumns } from './batchColumns';

const VISIBLE_ROWS = 20;

interface BatchTotals {
  totalItems: number;
  totalReviewed: number;
  totalNotReviewed: number;
}

const DATE_INPUT = 'YYYY-MM-DD';

function epochToDateInput(ms: number, tz: string): string {
  return moment.tz(ms, tz).format(DATE_INPUT);
}

interface Props {
  projectId: string;
}

export function HomeTab({ projectId }: Props) {
  const queryClient = useQueryClient();
  const { data: project } = useProject(projectId);
  const canManageBatches = project ? hasPermission(project, 'MANAGE_BATCHES') : false;
  const canReviewManagement = project ? hasPermission(project, 'REVIEW_MANAGEMENT') : false;

  const { data: reviewStats } = useReviewStatistics(projectId, { enabled: canReviewManagement });
  const setReviewDateRange = useSetReviewDateRange(projectId);

  const [beginDate, setBeginDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const timezone = project?.timezoneId || moment.tz.guess();

  useEffect(() => {
    if (reviewStats) {
      setBeginDate(epochToDateInput(reviewStats.begin, timezone));
      setEndDate(epochToDateInput(reviewStats.end, timezone));
    }
  }, [reviewStats, timezone]);

  const [assignBatch, setAssignBatch] = useState<import('../../hooks/useProjectBatches').Batch | null>(null);
  const [editBatch, setEditBatch] = useState<import('../../hooks/useProjectBatches').Batch | null>(null);
  const [currentPage, setCurrentPage] = useState(0);

  // isPlaceholderData (not isFetching) so the grid only dims on page changes,
  // not on the background status poll.
  const { data: batchPage, isLoading: loadingBatches, isPlaceholderData: showingPrevPage } = useProjectBatches(projectId, currentPage, VISIBLE_ROWS);

  const batches = batchPage?.batches ?? [];
  const totalBatches = batchPage?.totalBatches ?? 0;
  const pinnedRows: BatchTotals[] = batchPage ? [{
    totalItems: batchPage.totalItems,
    totalReviewed: batchPage.totalReviewed,
    totalNotReviewed: batchPage.totalNotReviewed,
  }] : [];

  const totalPages = totalBatches > 0 ? Math.ceil(totalBatches / VISIBLE_ROWS) : 0;

  const invalidateBatches = () => queryClient.invalidateQueries({ queryKey: ['batches', projectId] });

  function deleteBatch(batch: Batch) {
    if (!window.confirm(`Are you sure you want to permanently delete the batch "${batch.name}"?`)) return;
    api.delete(`/project/${projectId}/tag/${batch.id}`).then(invalidateBatches).catch((err) => showApiErrorToast(err));
  }

  const columnDefs = useMemo(
    () => buildBatchColumns({ projectId, canManageBatches }),
    [projectId, canManageBatches]
  );

  const handleDateChange = (begin: string, end: string) => {
    const b = moment.tz(begin, DATE_INPUT, true, timezone).startOf('day');
    const e = moment.tz(end, DATE_INPUT, true, timezone).endOf('day');
    if (b.isValid() && e.isValid()) {
      setReviewDateRange.mutate({ begin: b.toDate(), end: e.toDate() });
    }
  };

  return (
    <>
      {canReviewManagement && (
        <div>
          <div className="row">
            <div className="col-md-12">
              <h2 data-testid="project-home-name-heading">
                <i className="fas fa-home" aria-hidden="true" />
                {' '}{project?.name} Home
              </h2>
            </div>
          </div>

          <div style={{ display: 'table' }}>
            <div style={{ display: 'table-cell' }}>
              <h2 className="pull-left">Burn Down Chart [ between</h2>
            </div>
            <div style={{ display: 'table-cell', verticalAlign: 'middle', paddingTop: 15, paddingLeft: 15 }}>
              <input
                type="date"
                className="form-control"
                style={{ display: 'inline-block', width: 'auto' }}
                value={beginDate}
                data-testid="project-home-burndown-start-input"
                onChange={e => {
                  setBeginDate(e.target.value);
                  handleDateChange(e.target.value, endDate);
                }}
              />
            </div>
            <div style={{ display: 'table-cell', verticalAlign: 'middle', paddingLeft: 15 }}>
              <h2>and</h2>
            </div>
            <div style={{ display: 'table-cell', verticalAlign: 'middle', paddingTop: 15, paddingLeft: 15 }}>
              <input
                type="date"
                className="form-control"
                style={{ display: 'inline-block', width: 'auto' }}
                value={endDate}
                data-testid="project-home-burndown-end-input"
                onChange={e => {
                  setEndDate(e.target.value);
                  handleDateChange(beginDate, e.target.value);
                }}
              />
            </div>
            <div style={{ display: 'table-cell', verticalAlign: 'middle', paddingLeft: 5 }}>
              <h2>]</h2>
            </div>
          </div>

          {project?.hasBatches && reviewStats ? (
            <BurnDownChart reviewStats={reviewStats} />
          ) : project?.hasBatches ? (
            <LoadingPlaceholder />
          ) : (
            <div style={{ position: 'relative' }}>
              <img
                src="../images/burn_down_chart_placeholder.png"
                style={{ width: '100.33%', opacity: 0.2, marginTop: -8 }}
                alt=""
              />
              <h2 style={{ position: 'absolute', top: '35%', left: '40%' }}>
                <span className="label label-default">No Batches Created</span>
              </h2>
            </div>
          )}
        </div>
      )}

      <h2
        className="pull-left"
        style={project?.hasBatches ? { marginTop: 24 } : { marginTop: 13 }}
        data-testid="batch-list-title-heading"
      >
        {canManageBatches ? 'All Batches' : 'My Batches'}
      </h2>

      {totalBatches === 0 && !loadingBatches && (
        <div className="row">
          <div className="col-md-12">
            <h5>{canManageBatches ? 'None Available' : 'None Assigned'}</h5>
          </div>
        </div>
      )}

      {totalBatches > 0 && (
        <div className="row">
          <div className="col-md-12" style={fetchingStyle(showingPrevPage)}>
            <PaginationControls currentPage={currentPage} totalPages={totalPages} onGoTo={setCurrentPage} testId="batch-list-pagination-top-list" />
            <IioDataGrid
              testId="batch-list-grid"
              rowModelType="clientSide"
              rowData={batches}
              columnDefs={columnDefs}
              pinnedTopRowData={pinnedRows}
              pinnedBottomRowData={pinnedRows}
              domLayout="autoHeight"
              height="auto"
              context={{
                openAssign: (batch: Batch) => setAssignBatch(batch),
                openEdit: (batch: Batch) => setEditBatch(batch),
                openDelete: deleteBatch,
              }}
            />
            <PaginationControls currentPage={currentPage} totalPages={totalPages} onGoTo={setCurrentPage} testId="batch-list-pagination-bottom-list" />
          </div>
        </div>
      )}

      {assignBatch && (
        <AssignBatchModal
          projectId={projectId}
          batch={assignBatch}
          onClose={() => setAssignBatch(null)}
          onAssigned={() => { setAssignBatch(null); invalidateBatches(); }}
        />
      )}
      {editBatch && (
        <EditBatchModal
          projectId={projectId}
          batch={editBatch}
          onClose={() => { setEditBatch(null); invalidateBatches(); }}
        />
      )}
    </>
  );
}
